"use client";

import Link from "next/link";
import { usePathname } from "next/navigation";
import {
  LayoutDashboard,
  GitBranch,
  MessageSquare,
  Users,
  Settings,
  Zap,
  Bell,
  KanbanSquare,
  Shield,
} from "lucide-react";
import { useUIStore } from "@/stores/ui-store";
import { useAuthStore } from "@/stores/auth-store";
import { usePendingAlertsCount } from "@/hooks/use-queries";
import { cn } from "@/lib/utils";

const navigation = [
  { name: "Panel", href: "/", icon: LayoutDashboard },
  { name: "Flujos", href: "/flows", icon: GitBranch },
  { name: "Conversaciones", href: "/conversations", icon: MessageSquare },
  { name: "Leads", href: "/leads", icon: Users },
  { name: "Negocios", href: "/pipeline", icon: KanbanSquare },
  { name: "Alertas", href: "/alerts", icon: Bell },
  { name: "Demo", href: "/demo", icon: Zap },
  { name: "Configuración", href: "/settings", icon: Settings },
];

export function Sidebar() {
  const pathname = usePathname();
  const { sidebarCollapsed, toggleSidebar } = useUIStore();
  const { user } = useAuthStore();
  const { data: alertsData } = usePendingAlertsCount();
  const pendingCount = alertsData?.count ?? 0;
  const items = user?.role === "platform_admin"
    ? [...navigation, { name: "Tenants", href: "/platform", icon: Shield }]
    : navigation;

  return (
    <aside
      className={cn(
        "flex h-screen flex-col border-r border-sidebar-border bg-sidebar transition-all duration-200",
        sidebarCollapsed ? "w-[60px]" : "w-60"
      )}
    >
      {/* Brand */}
      <div
        className={cn(
          "flex h-14 items-center border-b border-sidebar-border/50 px-4 cursor-pointer select-none",
          sidebarCollapsed && "justify-center px-0"
        )}
        onClick={toggleSidebar}
      >
        {sidebarCollapsed ? (
          <img
            src="/branding/xperionai-icon.jpg"
            alt="XperionAI"
            className="h-7 w-7 shrink-0 rounded-lg object-cover ring-1 ring-sidebar-primary/35"
          />
        ) : (
          <img
            src="/branding/xperionai-logo-horizontal.svg"
            alt="XperionAI"
            className="h-7 w-36 object-contain object-left"
          />
        )}
      </div>

      {/* Navigation */}
      <nav className="flex-1 space-y-0.5 p-2 pt-3">
        {items.map((item) => {
          const isActive =
            item.href === "/"
              ? pathname === "/"
              : pathname.startsWith(item.href);
          const isAlerts = item.href === "/alerts";

          return (
            <Link
              key={item.name}
              href={item.href}
              title={sidebarCollapsed ? item.name : undefined}
              className={cn(
                "group relative flex items-center gap-3 rounded-md px-2.5 py-2 text-sm font-medium transition-all duration-150",
                isActive
                  ? "bg-sidebar-primary/15 text-sidebar-primary ring-1 ring-sidebar-primary/25"
                  : "text-sidebar-foreground/55 hover:bg-sidebar-accent hover:text-sidebar-accent-foreground",
                sidebarCollapsed && "justify-center px-0 py-2.5"
              )}
            >
              <span className="relative shrink-0">
                <item.icon
                  className={cn(
                    "h-4 w-4 transition-colors",
                    isActive
                      ? "text-sidebar-primary"
                      : "text-sidebar-foreground/35 group-hover:text-sidebar-accent-foreground"
                  )}
                />
                {isAlerts && pendingCount > 0 && (
                  <span className="absolute -right-1 -top-1 flex h-3.5 min-w-3.5 items-center justify-center rounded-full bg-red-500 px-0.5 text-[8px] font-bold text-white leading-none">
                    {pendingCount > 99 ? "99+" : pendingCount}
                  </span>
                )}
              </span>
              {!sidebarCollapsed && (
                <span className="flex-1">{item.name}</span>
              )}
              {!sidebarCollapsed && isAlerts && pendingCount > 0 && (
                <span className="ml-auto flex h-4.5 min-w-[18px] items-center justify-center rounded-full bg-red-500/20 px-1 text-[10px] font-semibold text-red-400">
                  {pendingCount}
                </span>
              )}
            </Link>
          );
        })}
      </nav>

      {/* Footer */}
      {!sidebarCollapsed && (
        <div className="p-3 border-t border-sidebar-border/50">
          <p className="text-[10px] text-sidebar-foreground/30 tracking-wider uppercase">
            v0.1 · MVP
          </p>
        </div>
      )}
    </aside>
  );
}
